import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { tokens } from "../../theme";
import VodWizardDialog from "../../components/distribution/VodWizardDialog";
import {
  foyerApi,
  type OwnerShell,
  type OwnerTitleRow,
} from "../../http/foyer";

/**
 * "My titles" — the one page the supplier shell has today (`CONTENT-4`).
 *
 * Everything on it comes from `GET /owner/vod-titles`, which is paged from
 * Foyer's OWN ownership mapping rather than by filtering an upstream page
 * (`CONTENT-2`). So the total is honest, and a title that belongs to somebody
 * else is not merely hidden here — it is a 404 at the API.
 *
 * Upload (`CONTENT-9`), Reach (`CONTENT-10`), Rentals (`CONTENT-11`) and
 * Analytics (`ANALYTICS-3`) arrive with their own tickets. They are deliberately
 * absent from the nav rather than present and empty: a menu entry that leads
 * nowhere teaches a partner that the product is a mock-up.
 */

interface ShellContext {
  shell: OwnerShell;
}

/** Swedish-free, partner-facing wording for the derived availability state. */
const AVAILABILITY_LABEL: Record<string, string> = {
  available: "Live",
  scheduled: "Scheduled",
  expired: "Withdrawn",
  unrestricted: "Live everywhere",
  unknown: "Unknown",
};

export default function SupplierTitlesPage() {
  const { shell } = useOutletContext<ShellContext>();
  const [rows, setRows] = useState<OwnerTitleRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [wizard, setWizard] = useState(false);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let live = true;
    foyerApi
      .listOwnTitles()
      .then((res) => live && setRows(res.titles))
      .catch(
        (e) => live && setError(e instanceof Error ? e.message : String(e))
      );
    return () => {
      live = false;
    };
  }, [reloads]);

  /**
   * The SAME wizard the CMS uses, bound to the owner routes.
   *
   * A second supplier wizard would drift within a release, and the drift would
   * be silent — a step quietly relaxed on one side. The three things that
   * differ all come from the server: which base the calls go to, whether
   * `packages` is a step (it is `skipped` here), and what the last button
   * promises.
   */
  const wizardDialog = (
    <VodWizardDialog
      open={wizard}
      surface="owner"
      onClose={() => setWizard(false)}
      onFinished={() => setReloads((n) => n + 1)}
    />
  );

  if (error) return <Alert severity="error">{error}</Alert>;

  /**
   * 🚨 The wizard is mounted ONCE, outside the branch (see the bottom of this
   * file). Everything from here down is the page BEHIND it.
   *
   * It used to be rendered inside each branch, and that quietly broke the
   * first title anybody ever created: finishing step one bumps `reloads`, the
   * list comes back with one row, `rows.length` goes 0 → 1, and the component
   * swaps from the empty-state return to the table return. React reconciles by
   * position, so the whole subtree — the open dialog included — was UNMOUNTED
   * and a fresh one mounted in its place.
   *
   * A remount is a fresh `useState`: `assetId` back to null, the draft empty,
   * the ladder gone, every step past the first locked again. The title was
   * created; the wizard simply forgot it, with nothing on screen to say so.
   * Reported as *"it didn't move on in the flow, just threw me back to a list
   * of files — no error no nothing"*, and "no error" is exactly right: nothing
   * failed.
   *
   * It only ever bit on an account with NO titles, which is every account the
   * first time somebody uses it.
   */
  const body =
    rows && rows.length === 0 ? (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography sx={{ fontSize: 22, mb: 1 }}>Nothing here yet</Typography>
        {/* Says what happens next - and, since `CONTENT-9` shipped, the next
            thing is a button on this screen rather than an email. Telling a
            partner to contact somebody, when the product can do the thing, is
            the product admitting it has not shipped. */}
        <Typography sx={{ color: tokens.inkMuted, maxWidth: 460, mx: "auto" }}>
          Titles you deliver to Nenda appear here once they are in the
          catalogue, with the markets they are licensed for. Upload a folder to
          get your first one in.
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          sx={{ mt: 2 }}
        >
          <Button
            variant="contained"
            onClick={() => setWizard(true)}
            data-testid="titles-empty-single"
          >
            Deliver one title
          </Button>
          <Button
            component={Link}
            to="/supplier/upload"
            variant="outlined"
            data-testid="titles-empty-upload"
          >
            Upload a folder
          </Button>
        </Stack>
      </Box>
    ) : (
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <Typography sx={{ fontSize: 24 }}>{shell.owner.name}</Typography>
            <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
              Everything Nenda is carrying from you.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => setWizard(true)}
            data-testid="titles-deliver"
          >
            Deliver a title
          </Button>
        </Stack>

        <TableContainer
          sx={{
            background: tokens.surface,
            border: `1px solid ${tokens.divider}`,
            borderRadius: 2,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: tokens.inkMuted }}>Title</TableCell>
                <TableCell sx={{ color: tokens.inkMuted, width: 120 }}>
                  Type
                </TableCell>
                <TableCell sx={{ color: tokens.inkMuted, width: 160 }}>
                  Availability
                </TableCell>
                <TableCell sx={{ color: tokens.inkMuted, width: 160 }}>
                  Reference
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rows ?? []).map((row) => (
                <TableRow key={row.assetId} hover>
                  <TableCell>
                    {/* The row is the way in: a list of titles you cannot open
                      is a list, not a surface. */}
                    <Link
                      to={`/supplier/titles/${encodeURIComponent(row.assetId)}`}
                      style={{ color: tokens.ink }}
                    >
                      {row.originalTitle || row.assetId}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {row.type}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        AVAILABILITY_LABEL[row.availability?.state ?? "unknown"]
                      }
                      sx={{
                        background: tokens.brandTintSoft,
                        color: tokens.ink,
                      }}
                    />
                    {/* CONTENT-28: licensed by Nenda - visible, read-only. */}
                    {row.licensed && (
                      <Chip
                        size="small"
                        label="Licensed"
                        title="A title Nenda has licensed. Changes to it go through Nenda."
                        data-testid={`title-licensed-${row.assetId}`}
                        sx={{ ml: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkFaint }}>
                    {row.reference ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    );

  /**
   * ONE mount, outside every branch, so switching between the empty state and
   * the table cannot unmount an open wizard mid-flow.
   */
  return (
    <>
      {body}
      {wizardDialog}
    </>
  );
}
